/**
 * The editor's "Failing jobs" field (`on.job_failed`). With the project's
 * CI jobs (from GitLab's lint API, `editor-options`), each job is a
 * checkbox, grouped by stage; any other line is a custom pattern, shown
 * with the jobs it matches today. Without them (GitLab could not list the
 * jobs), the patterns are plain text, one per line, with the reason.
 *
 * The value stays the form's one-pattern-per-line text, in its order: a
 * picked job is appended, an unpicked one removed. A picked job is written
 * as the pattern matching exactly it (`jobPatternFor`): a name holding `[`,
 * `*` or `?` would otherwise be read as a glob and miss its own job.
 */

import { useState, type ReactElement } from 'react';
import { jobPatternFor, matchesJob, patternList } from '../utils/jobPattern';
import type { CiJob } from '../schemas/editorOptions.schemas';

interface JobPickerProps {
  id: string;
  patterns: string;
  /** null: no job list, so the patterns are typed. */
  jobs: CiJob[] | null;
  /** Why there is no list, or that it was cut. */
  note: string | null;
  onChange: (patterns: string) => void;
}

const FILTER_FROM = 8;
const HINT = 'Pick the jobs whose failure starts a run, or add a pattern: * matches any text.';

function byStage(jobs: CiJob[]): [string, CiJob[]][] {
  const stages = new Map<string, CiJob[]>();
  for (const job of jobs) stages.set(job.stage, [...(stages.get(job.stage) ?? []), job]);
  return [...stages.entries()];
}

function Custom(props: { pattern: string; jobs: CiJob[]; onRemove: () => void }): ReactElement {
  const matched = props.jobs.filter((j) => matchesJob(props.pattern, j.name)).map((j) => j.name);
  return (
    <li className="def-form__pattern">
      <code>{props.pattern}</code>
      <span className="def-form__pattern-matches">
        {matched.length === 0 ? 'matches no current job' : `matches ${matched.join(', ')}`}
      </span>
      <button type="button" className="secondary-button" aria-label={`Remove pattern ${props.pattern}`} onClick={props.onRemove}>
        Remove
      </button>
    </li>
  );
}

export function JobPicker({ id, patterns, jobs, note, onChange }: JobPickerProps): ReactElement {
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState('');
  if (jobs === null) {
    return (
      <div className="form-group def-form__field">
        <label htmlFor={id}>Failing jobs (one pattern per line)</label>
        <textarea id={id} className="def-form__textarea" rows={3} spellCheck={false} value={patterns}
          aria-describedby={`${id}-hint`} onChange={(e) => { onChange(e.target.value); }} />
        <span id={`${id}-hint`} className="def-form__hint">
          Job names; * matches any text.{note !== null && ` (${note}.)`}
        </span>
      </div>
    );
  }
  const list = patternList(patterns);
  const picked = new Set(jobs.map((j) => jobPatternFor(j.name)));
  const custom = list.filter((p) => !picked.has(p));
  const set = (next: string[]): void => { onChange(next.join('\n')); };
  const toggle = (name: string): void => {
    const pattern = jobPatternFor(name);
    set(list.includes(pattern) ? list.filter((p) => p !== pattern) : [...list, pattern]);
  };
  const add = (): void => {
    const pattern = adding.trim();
    if (pattern !== '' && !list.includes(pattern)) set([...list, pattern]);
    setAdding('');
  };
  const shown = jobs.filter((j) => j.name.toLowerCase().includes(filter.trim().toLowerCase()));
  return (
    <fieldset className="def-form__jobs" aria-describedby={`${id}-hint`}>
      <legend>Failing jobs</legend>
      {jobs.length >= FILTER_FROM && (
        <input type="search" className="def-form__job-filter" placeholder="Filter jobs" aria-label="Filter jobs"
          value={filter} onChange={(e) => { setFilter(e.target.value); }} />
      )}
      <div className="def-form__job-list">
        {jobs.length === 0 && <span className="def-form__hint">This project's CI has no job a run can match.</span>}
        {byStage(shown).map(([stage, stageJobs]) => (
          <div key={stage} className="def-form__stage">
            <span className="def-form__stage-name">{stage || 'no stage'}</span>
            {stageJobs.map((job) => {
              const via = custom.find((p) => matchesJob(p, job.name));
              const on = list.includes(jobPatternFor(job.name));
              return (
                <label key={job.name} className="def-form__check">
                  <input type="checkbox" checked={on} onChange={() => { toggle(job.name); }} />
                  {job.name}
                  {via !== undefined && !on && <span className="def-form__via"> · via {via}</span>}
                </label>
              );
            })}
          </div>
        ))}
      </div>
      {custom.length > 0 && (
        <ul className="def-form__patterns" aria-label="Custom patterns">
          {custom.map((p) => (
            <Custom key={p} pattern={p} jobs={jobs} onRemove={() => { set(list.filter((x) => x !== p)); }} />
          ))}
        </ul>
      )}
      <div className="def-form__add-pattern">
        <input type="text" aria-label="Add a pattern" placeholder="e.g. test:*" autoComplete="off" spellCheck={false}
          value={adding} onChange={(e) => { setAdding(e.target.value); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" className="secondary-button" onClick={add} disabled={adding.trim() === ''}>Add pattern</button>
      </div>
      <span id={`${id}-hint`} className="def-form__hint">{HINT}{note !== null && ` ${note}.`}</span>
    </fieldset>
  );
}
